import { v4 as uuid } from "uuid"
import * as base64 from "../utils/base64"
import {
    StorageKeyCSRFToken,
    StorageKeyOIDCClientID,
    StorageKeyOIDCRedirectUri,
    StorageKeyPKCECodeVerifier,
} from "./constants"

export type LogInWithOAuthParams = {
    clientId: string
    redirectUri: string
}

export function prepareRedirectFlow(params: LogInWithOAuthParams) {
    // set up the client ID and redirect URI in the session storage
    window.sessionStorage.setItem(StorageKeyOIDCClientID, params.clientId)
    window.sessionStorage.setItem(StorageKeyOIDCRedirectUri, params.redirectUri)
}

export function getRedirectFlowParams(): LogInWithOAuthParams {
    const clientId = window.sessionStorage.getItem(StorageKeyOIDCClientID)
    const redirectUri = window.sessionStorage.getItem(StorageKeyOIDCRedirectUri)

    if (!clientId || !redirectUri) {
        // TODO make this a SlashID error
        throw new Error("Client ID or redirect URI not found in session storage")
    }

    return { clientId, redirectUri }
}

export function cleanUpRedirectFlow() {
    window.sessionStorage.removeItem(StorageKeyOIDCClientID)
    window.sessionStorage.removeItem(StorageKeyOIDCRedirectUri)
}

/**
 * Prepare a CSRF token and PKCE code challenge for use in the OAuth 2.0 authorization code flow.
 * This is required for public clients (e.g. single-page applications).
 */
export async function prepareCSRFToken() {
    const csrf_token = uuid()
    const pkce_code_verifier = uuid() + uuid() // recommended increased entropy
    const data = new TextEncoder().encode(pkce_code_verifier)
    const hash = await window.crypto.subtle.digest("SHA-256", data)
    const pkce_code_challenge = base64.encode(new Uint8Array(hash))

    // TODO side effect, consider moving outside
    window.localStorage.setItem(StorageKeyCSRFToken, csrf_token)
    window.localStorage.setItem(StorageKeyPKCECodeVerifier, pkce_code_verifier)

    return { csrf_token, pkce_code_challenge }
}

export type OAuthParams = {
    ssoCode: string
    csrfToken: string
}

export function getOAuthParams(queryString: string) {
    const urlParams = new URLSearchParams(queryString)
    const ssoCode = urlParams.get("code")
    // TODO this is specific to our flow
    const csrfToken = urlParams.get("state")

    if (!ssoCode || !csrfToken) {
        return null
    }

    return { ssoCode, csrfToken }
}

export type ObtainAccessTokenInput = LogInWithOAuthParams &
    OAuthParams & {
        apiBaseURL: string
    }

type PostOauth2TokensResponse = {
    access_token: string
    id_token: string
    token_type: string
    expires_in: number
    refresh_token: string
    scope: string
}

/**
 * Obtain the access token with the OAuth 2.0 authorization code flow.
 * Ensure security through PKCE (Proof Key for Code Exchange) and CSRF tokens.
 * CSRF goes through the state param.
 */
export async function obtainAccessToken(input: ObtainAccessTokenInput): Promise<PostOauth2TokensResponse> {
    const csrf_token = window.localStorage.getItem(StorageKeyCSRFToken)
    const pkce_code_verifier = window.localStorage.getItem(StorageKeyPKCECodeVerifier)

    if (!csrf_token || !pkce_code_verifier) {
        throw new Error("CSRF token or PKCE code verifier not found")
    }

    if (csrf_token !== input.csrfToken) {
        throw new Error("CSRF token mismatch")
    }

    const payload = new URLSearchParams()
    payload.set("client_id", input.clientId)
    payload.set("code", input.ssoCode)
    payload.set("code_verifier", pkce_code_verifier)
    payload.set("grant_type", "authorization_code")
    payload.set("redirect_uri", input.redirectUri)

    // TODO proper error handling
    const response = await fetch(`${input.apiBaseURL}/oauth2/tokens`, {
        method: "POST",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
        },
        body: payload.toString(),
    })

    if (!response.ok) {
        // TODO throw proper error
        throw new Error("Failed to obtain access token")
    }

    const data: PostOauth2TokensResponse = await response.json()
    return data
}
